import { useInfiniteQuery, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { COMMAND_CENTER_MESSAGES_QUERY_KEY } from "@/lib/command-center-realtime";
import { CommandCenterAPI, type CommandCenterMessage, type CommandCenterMessagePage } from "@/services/command-center";

export type MessagesData = InfiniteData<CommandCenterMessagePage>;

export function useCommandCenterMessagesQuery(refetchInterval: number | false) {
  return useInfiniteQuery<CommandCenterMessagePage>({
    queryKey: COMMAND_CENTER_MESSAGES_QUERY_KEY,
    queryFn: ({ pageParam }) => CommandCenterAPI.getMessages(typeof pageParam === "string" ? pageParam : undefined),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval,
    // Realtime keeps page 0 fresh; a background refetch would drop the optimistic rows.
    refetchOnWindowFocus: false,
  });
}

/** Newest first, as the API returns and the thread renders bottom-up. */
export function flattenMessages(data: MessagesData | undefined): CommandCenterMessage[] {
  return data ? data.pages.flatMap((p) => p.data) : [];
}

/** Puts a message at the top of page 0 unless an entry with the same id is already cached. */
export function insertMessage(queryClient: QueryClient, message: CommandCenterMessage): void {
  queryClient.setQueryData<MessagesData>(COMMAND_CENTER_MESSAGES_QUERY_KEY, (current) => {
    if (!current) return { pages: [{ data: [message], next_cursor: null }], pageParams: [undefined] };
    if (current.pages.some((p) => p.data.some((m) => m.id === message.id))) return current;
    const [first, ...rest] = current.pages;
    return { ...current, pages: [{ ...first, data: [message, ...first.data] }, ...rest] };
  });
}

/**
 * Replaces a message by id (used to reconcile the optimistic bubble).
 *
 * A poll or a refetch answering between the click and the response rewrites
 * page 0 from the server, and the temporary row is gone. There is then nothing
 * to replace, so the message is inserted instead — otherwise the post the API
 * accepted would not appear until the next refetch.
 */
export function replaceMessage(queryClient: QueryClient, id: string, message: CommandCenterMessage): void {
  let replaced = false;

  queryClient.setQueryData<MessagesData>(COMMAND_CENTER_MESSAGES_QUERY_KEY, (current) => {
    if (!current) return current;
    if (!current.pages.some((p) => p.data.some((m) => m.id === id))) return current;
    replaced = true;
    const already = current.pages.some((p) => p.data.some((m) => m.id === message.id));
    return {
      ...current,
      pages: current.pages.map((p) => ({
        ...p,
        data: p.data.flatMap((m) => (m.id === id ? (already ? [] : [message]) : [m])),
      })),
    };
  });

  if (!replaced) insertMessage(queryClient, message);
}

/**
 * Replaces a cached message by its own id, in place.
 *
 * An agent placeholder becomes an answer without moving in the thread, so the
 * update is one `setQueryData` and never an invalidation: a refetch would drop
 * the optimistic rows the composer is holding.
 */
export function replaceMessageById(queryClient: QueryClient, message: CommandCenterMessage): void {
  queryClient.setQueryData<MessagesData>(COMMAND_CENTER_MESSAGES_QUERY_KEY, (current) => {
    if (!current || !current.pages.some((page) => page.data.some((m) => m.id === message.id))) return current;
    return {
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        data: page.data.map((m) => (m.id === message.id ? message : m)),
      })),
    };
  });
}

export function removeMessage(queryClient: QueryClient, id: string): void {
  queryClient.setQueryData<MessagesData>(COMMAND_CENTER_MESSAGES_QUERY_KEY, (current) =>
    current ? { ...current, pages: current.pages.map((p) => ({ ...p, data: p.data.filter((m) => m.id !== id) })) } : current
  );
}
